import React, { useCallback, useEffect, useState } from 'react';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import {
    createActivity,
    selectIsLoading,
} from '../../../store/slices/selectedActivitySlice';
import { useNotifications } from '../../../../common/context/grimsbyNotifications';
import useFormValidation from '../../../../common/utils/formValidation';
import {
    CreateActivityData,
    DeliverySession,
} from '../../../interfaces/activity';
import { FormSectionMode } from '../../../../common/constants/forms';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { Button, Form } from '@amzn/awsui-components-react-v2';
import ActivityFormSection from '../FormSections/ActivityFormSection';
import { selectUser } from '../../../../common/store/slices/userSlice';
import { handleScrollToError } from '../../../../common/utils/scrollToError';
import {
    ActivitySessionControlArrayFormValues,
    ActivityFormSectionProps,
    createActivityValidationConfig,
    SessionAttributeEditorItem,
    sessionAttributeValidationConfig,
    RegionShortName,
} from '../Common/Common';
import { ACTIVITY_PATH } from '../../../constants/path';
import { MeridiemFieldValue } from '../../../interfaces/meridiem';
import {
    generateActivityName,
    getActivityById,
} from '../../../services/activity-service';
import ActivityCreateAutoAssignInstructorOptOutModalProps from './ActivityCreateModals/ActivityCreateAutoAssignInstructorOptOutModal';
import { OptionDefinition } from '@amzn/awsui-components-react-v3/polaris/internal/components/option/interfaces';
import OrderServiceBypassModal from './OrderServiceBypassModal';

const dayInMilliseconds = 86400000;

export const getUTCMilliseconds = (
    dateString: string,
    timeString: string,
    deliveryTimezone: string,
) => {
    // if emtpy, return invalid number, so session date is set empty
    if (dateString === '') return NaN;
    // get the utc time for the delivery location's time
    dayjs.extend(utc);
    dayjs.extend(timezone);
    const utcTime = dayjs
        .tz(dateString + ' ' + timeString, deliveryTimezone)
        .utc();
    return utcTime.toDate().getTime();
};

export const convertTo24HourFormat = (
    time: string,
    meridiem: MeridiemFieldValue,
) => {
    if (!time) {
        return '';
    }

    let [hours, minutes] = time.split(':');

    if (hours === '12') {
        hours = '00';
    }

    if (meridiem === MeridiemFieldValue.Pm) {
        hours = `${parseInt(hours, 10) + 12}`;
    }

    return `${hours}:${minutes}`;
};

export const getDeliverySession = (
    sessionItem: SessionAttributeEditorItem,
    timeZone: string,
): DeliverySession => {
    const militaryStartTime = convertTo24HourFormat(
        sessionItem.startTime,
        sessionItem.startTimeMeridiemFieldOption,
    );
    const militaryEndTime = convertTo24HourFormat(
        sessionItem.endTime,
        sessionItem.endTimeMeridiemFieldOption,
    );

    const millisecondStartTime = getUTCMilliseconds(
        sessionItem.dateString,
        militaryStartTime,
        timeZone,
    );
    let millisecondEndTime = getUTCMilliseconds(
        sessionItem.dateString,
        militaryEndTime,
        timeZone,
    );

    // if the end time is earlier than the start time
    // it means we're out of the bounds of the previous 24 hours
    // we need to offset by a day
    if (millisecondStartTime > millisecondEndTime) {
        millisecondEndTime = millisecondEndTime + dayInMilliseconds;
    }

    const session: DeliverySession = {
        id: sessionItem.deliverySessionId,
        start_timestamp: millisecondStartTime / 1000,
        end_timestamp: millisecondEndTime / 1000,
    };
    if (sessionItem.instructors && sessionItem.instructors.length > 0) {
        session.instructors = sessionItem.instructors;
    }

    return session;
};

const ActivityCreateForm = ({
    createActivityFormState,
}: {
    createActivityFormState: Omit<CreateActivityData, 'delivery_sessions'>;
}) => {
    const isLoading = useSelector(selectIsLoading);
    const userProfile = useSelector(selectUser);
    const history = useHistory();
    const dispatch = useDispatch();
    const { addNotification } = useNotifications();

    const {
        isInvalid,
        isControlArrayInvalid,
        errors,
        controlArrayErrors,
        validateForm,
        validateFormControlArray,
    } = useFormValidation<
        Omit<CreateActivityData, 'delivery_sessions'>,
        ActivitySessionControlArrayFormValues
    >();

    const createActivityStateWithDefaultValues: Omit<
        CreateActivityData,
        'delivery_sessions'
    > = {
        ...createActivityFormState,
        operations_owner: userProfile?.profile.full_name || '',
        operations_owner_email: userProfile?.profile.email || '',
        scheduler: userProfile?.profile.full_name || '',
        scheduler_email: userProfile?.profile.email || '',
    };

    const [formValues, setFormValues] = useState<
        Omit<CreateActivityData, 'delivery_sessions'>
    >(createActivityStateWithDefaultValues);

    const [shortName, setShortName] = useState<string>('');
    const [countryCode, setCountryCode] = useState<string | undefined>('');

    const [courseDays, setCourseDays] = useState<number>();
    const [autoAssignInstructor, setAutoAssignInstructor] =
        useState<boolean>(false);

    const [showOptOutReason, setShowOptOutReason] = useState(false);
    const [optOutReasonExplanation, setOptOutReasonExplanation] = useState<
        string | undefined
    >('');

    const [optOutReason, setOptOutReason] = useState<OptionDefinition | null>(
        null,
    );
    const [partnerInitiative, setPartnerInitiative] = useState<string>('');

    // OrderService bypass modal state
    const [showBypassModal, setShowBypassModal] = useState(false);
    const [pendingActivityData, setPendingActivityData] =
        useState<CreateActivityData | null>(null);

    const handleCourseDaysChange = (courseDays: number) => {
        setCourseDays(courseDays);
    };

    const handleCountryCodeChange = (countryCode: string | undefined) => {
        setCountryCode(countryCode);
    };

    const handlePartnerInitiativeChange = (
        partnerInitiative: string | undefined,
    ) => {
        setPartnerInitiative(partnerInitiative);
    };

    const handleShortNameChange = (shortName: string) => {
        setShortName(shortName);
    };

    const handleAutoAssignInstructorChange = (
        autoAssignInstructor: boolean | undefined,
    ) => {
        setAutoAssignInstructor(autoAssignInstructor);
    };

    const [sessionAttributeEditorItems, setSessionAttributeEditorItems] =
        useState([
            {
                dateString: '',
                startTime: '',
                endTime: '',
                startTimeMeridiemFieldOption:
                    MeridiemFieldValue.Am as MeridiemFieldValue,
                endTimeMeridiemFieldOption:
                    MeridiemFieldValue.Am as MeridiemFieldValue,
            },
        ] as Array<SessionAttributeEditorItem>);

    const handleFieldEvent = useCallback(
        (changes: Partial<Omit<CreateActivityData, 'delivery_sessions'>>) => {
            setFormValues((values) => ({
                ...values,
                ...changes,
            }));
        },
        [],
    );

    const validateAndHandleFieldEvent = useCallback(
        (changes: Partial<Omit<CreateActivityData, 'delivery_sessions'>>) => {
            setFormValues((values) => ({
                ...values,
                ...changes,
            }));
        },
        [],
    );

    const handleSessionItemEvent = (
        sessionItems: Array<SessionAttributeEditorItem>,
    ) => {
        setSessionAttributeEditorItems(sessionItems);
    };

    const cancelModal = () => {
        setShowOptOutReason(false);
    };

    const handleAddOptOutReason = (e: any) => {
        handleFieldEvent({
            auto_assign_instructor_opt_out_reason: e.label,
        });
        setOptOutReason(e);
    };

    const getOptOutModal = async () => {
        const isVilt = formValues.activity_modality === 'vILT';
        const isCommercial = formValues.program === 'Commercial';
        const isPartnerProgram = formValues.program === 'Partner Program';
        const isAutoAssignInstructorDisabled = autoAssignInstructor === false;
        const isViltCommercialOrPartnerProgram =
            isVilt && (isCommercial || isPartnerProgram);

        const autoAssignInstructorOptOutIsVisible =
            isViltCommercialOrPartnerProgram && isAutoAssignInstructorDisabled;
        if (autoAssignInstructorOptOutIsVisible) {
            setShowOptOutReason(true);
        } else {
            handleCreateActivity();
        }
    };
    const handleCreateActivity = async () => {
        const invalid = validateForm(
            formValues,
            createActivityValidationConfig,
        );
        const arrayInvalid = validateFormControlArray(
            { sessionAttributeEditorItems },
            sessionAttributeValidationConfig,
        );
        const isActivityFormInvalid = invalid || arrayInvalid;
        formValues.course_days = courseDays ? courseDays : 1;
        if (!isActivityFormInvalid) {
            const activityData: CreateActivityData = {
                ...formValues,
                activity_name: generateActivityName(
                    formValues,
                    countryCode,
                    shortName,
                ),
                delivery_sessions: sessionAttributeEditorItems
                    .filter(
                        (item) =>
                            item.startTime && item.endTime && item.dateString,
                    )
                    .map((item) =>
                        getDeliverySession(item, formValues.delivery_timezone),
                    ),
                auto_assign_instructor: autoAssignInstructor,
                auto_assign_instructor_opt_out_reason:
                    optOutReason?.label !== 'Other'
                        ? optOutReason?.label
                        : `Other - ${optOutReasonExplanation.trim()}`,
            };

            await submitActivity(activityData);
        } else {
            handleScrollToError();
        }
    };

    const submitActivity = async (activityData: CreateActivityData) => {
        const result = await dispatch<any>(createActivity(activityData));
        const { isSuccessful, createdActivityId, errorText, statusCode } =
            result;

        if (isSuccessful) {
            addNotification({
                id: `create-activity-${Date.now()}`,
                type: 'success',
                content: 'You have successfully created an activity.',
            });
            history.push({
                pathname: `/activities/${createdActivityId}`,
            });
        } else if (statusCode === 412) {
            // OrderService order not found - show bypass modal
            setPendingActivityData(activityData);
            setShowBypassModal(true);
        } else {
            let errorMessage = 'An error occurred while creating the activity';

            if (errorText) {
                const lowercaseResponse = errorText.toLowerCase();
                const errorMessageTemplate = (type, typeName) =>
                    `Unable to create activity for this ${type}. Your account is not authorized to access the ${typeName} ${type}. Please contact your manager for assistance or select a different ${type}.`;

                if (lowercaseResponse.includes('program')) {
                    errorMessage = errorMessageTemplate(
                        'program',
                        formValues.program,
                    );
                } else if (lowercaseResponse.includes('delivery_region')) {
                    const regionKey =
                        formValues.delivery_region as keyof typeof RegionShortName;
                    const fullRegionName =
                        regionKey && RegionShortName[regionKey]
                            ? RegionShortName[regionKey]
                            : regionKey;
                    errorMessage = errorMessageTemplate(
                        'region',
                        fullRegionName,
                    );
                } else {
                    errorMessage = errorText;
                }
            }

            addNotification({
                id: `create-activity-error-${Date.now()}`,
                type: 'error',
                content: errorMessage,
            });
        }
    };

    const handleBypassOrderService = async (bypassReason: string) => {
        if (!pendingActivityData) return;

        // Add order_bypass_reason to the activity data
        const activityDataWithBypass: CreateActivityData = {
            ...pendingActivityData,
            order_bypass_reason: bypassReason,
        };

        // Close the modal
        setShowBypassModal(false);

        // Retry the activity creation with bypass reason
        await submitActivity(activityDataWithBypass);

        // Clear pending data
        setPendingActivityData(null);
    };

    const handleCancelBypass = () => {
        setShowBypassModal(false);
        setPendingActivityData(null);
    };
    const createActivityProps: ActivityFormSectionProps<
        Omit<CreateActivityData, 'delivery_sessions'>
    > & {
        handleShortNameChange: (shortName: string) => void;
        handleCountryCodeChange: (countryCode: string | undefined) => void;
        handleCourseDaysChange: (courseDays: number) => void;
        handleAutoAssignInstructorChange: (
            autoAssignInstructor: boolean | undefined,
        ) => void;
        handlePartnerInitiativeChange: (
            partnerInitiative: string | undefined,
        ) => void;
    } = {
        formValues,
        errors,
        handleFieldEvent,
        validateAndHandleFieldEvent,
        handleSessionItemEvent,
        sessionAttributeEditorItems,
        mode: FormSectionMode.Create,
        controlArrayErrors,
        handleShortNameChange,
        handleCourseDaysChange,
        handleCountryCodeChange,
        handleAutoAssignInstructorChange,
        handlePartnerInitiativeChange,
    };

    useEffect(() => {
        if (isInvalid) {
            validateForm(formValues, createActivityValidationConfig);
        }

        if (isControlArrayInvalid) {
            validateFormControlArray(
                { sessionAttributeEditorItems },
                sessionAttributeValidationConfig,
            );
        }
    }, [
        isInvalid,
        formValues,
        validateForm,
        validateFormControlArray,
        sessionAttributeEditorItems,
        isControlArrayInvalid,
    ]);

    return (
        <section data-testid="ActivityCreateForm">
            <Form
                header="Add activity"
                actions={
                    <div className="awsui-util-action-stripe awsui-util-mb-m">
                        <div className="awsui-util-action-stripe-group">
                            <Button
                                variant="link"
                                className="admin-activity-cancel"
                                data-testid="ActivityCreateFormCancel"
                                onClick={() => {
                                    history.push({
                                        pathname: ACTIVITY_PATH.BASE,
                                    });
                                }}
                            >
                                Cancel
                            </Button>
                            <Button
                                variant="primary"
                                className="admin-activity-save"
                                data-testid="ActivityCreateFormAdd"
                                loading={isLoading}
                                onClick={getOptOutModal}
                            >
                                {`${isLoading ? 'Adding' : 'Add'} activity`}
                            </Button>
                        </div>
                    </div>
                }
            >
                <ActivityFormSection {...createActivityProps} />
            </Form>
            <ActivityCreateAutoAssignInstructorOptOutModalProps
                visible={showOptOutReason}
                data-testid="ActivityCreateAutoAssignInstructorOptOutModalProps"
                onCancel={cancelModal}
                onConfirm={handleCreateActivity}
                setOptOutReason={handleAddOptOutReason}
                optOutReason={optOutReason}
                setOptOutReasonExplanation={setOptOutReasonExplanation}
                optOutReasonExplanation={optOutReasonExplanation}
            />
            <OrderServiceBypassModal
                visible={showBypassModal}
                onCancel={handleCancelBypass}
                onBypass={handleBypassOrderService}
                isLoading={isLoading}
            />
        </section>
    );
};

export default ActivityCreateForm;
